import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useDocumentDetail, useUpdateTranslation } from "./use-documents";
import { TOS_TRANSLATION_BODY_MAX, TOS_TRANSLATION_TITLE_MAX } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";
import { MarkdownPreview } from "../shared/MarkdownPreview";

export function TranslationEditorPage() {
  const { id: tosId, locale } = useParams<{ id: string; locale: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const detail = useDocumentDetail(tosId);
  const update = useUpdateTranslation();

  const translation = useMemo(
    () => detail.data?.translations?.find((tr) => tr.localeCode === locale) ?? null,
    [detail.data, locale],
  );

  // A retired document's translations are read-only even for admins:
  // saving has no value (no user will ever sign this version again)
  // and would silently drift the bytes from what users accepted.
  // SCHEDULED_RETIRE is still live, so translations stay mutable
  // until the scheduled effectiveUntil passes.
  const isRetired = detail.data?.state === "RETIRED";
  const canMutate = canEdit && !isRetired;

  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (translation) {
      setTitle(translation.title);
      setBodyMarkdown(translation.bodyMarkdown);
      setError(null);
    }
  }, [translation]);

  if (detail.isLoading) {
    return (
      <CompliancePageShell
        screenTestId="compliance-translation-editor-screen"
        titleKey="compliance.documents.translations.editorTitle"
        subtitleKey="compliance.documents.translations.editorSubtitle"
      >
        <p className="text-sm text-muted-foreground">{t("compliance.documents.detail.loading")}</p>
      </CompliancePageShell>
    );
  }

  if (!detail.data || !translation) {
    return (
      <CompliancePageShell
        screenTestId="compliance-translation-editor-screen"
        titleKey="compliance.documents.translations.editorTitle"
        subtitleKey="compliance.documents.translations.editorSubtitle"
      >
        <p data-testid="compliance-translation-not-found" className="text-sm text-destructive">
          {t("compliance.documents.translations.notFound")}
        </p>
      </CompliancePageShell>
    );
  }

  const trimmedTitle = title.trim();
  const trimmedBody = bodyMarkdown.trim();
  const isDirty = trimmedTitle !== translation.title || trimmedBody !== translation.bodyMarkdown;
  const isValid =
    trimmedTitle.length > 0 &&
    trimmedTitle.length <= TOS_TRANSLATION_TITLE_MAX &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= TOS_TRANSLATION_BODY_MAX;

  const handleSave = async () => {
    if (!canMutate || !isDirty || !isValid || update.isPending) return;
    setError(null);
    try {
      await update.mutateAsync({
        tosId: detail.data!.id,
        locale: translation.localeCode,
        input: {
          title: trimmedTitle !== translation.title ? trimmedTitle : undefined,
          bodyMarkdown: trimmedBody !== translation.bodyMarkdown ? trimmedBody : undefined,
        },
      });
      navigate(`/compliance/documents/${detail.data!.id}`);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <CompliancePageShell
      screenTestId="compliance-translation-editor-screen"
      titleKey="compliance.documents.translations.editorTitle"
      subtitleKey="compliance.documents.translations.editorSubtitle"
    >
      <div>
        <Link
          to={`/compliance/documents/${detail.data.id}`}
          data-testid="compliance-translation-back-link"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          {t("compliance.documents.translations.backToDocument")}
        </Link>
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
        <p>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {translation.localeCode}
          </code>{" "}
          · v{detail.data.version} · {t(`compliance.roles.${detail.data.roleScope}`)}
        </p>
      </div>

      {isRetired && (
        <div
          data-testid="compliance-translation-retired-banner"
          className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
        >
          <span aria-hidden="true">🔒</span>
          <p>
            {t("compliance.documents.translations.retiredBanner", {
              at: new Date(detail.data.effectiveUntil ?? "").toLocaleString(),
            })}
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="tr-edit-title">{t("compliance.documents.translations.titleField")}</Label>
        <Input
          id="tr-edit-title"
          data-testid="compliance-translation-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TOS_TRANSLATION_TITLE_MAX}
          readOnly={!canMutate}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="tr-edit-body">{t("compliance.documents.translations.bodyField")}</Label>
          <Textarea
            id="tr-edit-body"
            data-testid="compliance-translation-body-input"
            value={bodyMarkdown}
            onChange={(e) => setBodyMarkdown(e.target.value)}
            maxLength={TOS_TRANSLATION_BODY_MAX}
            rows={20}
            readOnly={!canMutate}
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-2">
          <Label>{t("compliance.documents.translations.preview")}</Label>
          <MarkdownPreview
            data-testid="compliance-translation-preview"
            value={bodyMarkdown}
            emptyPlaceholder={t("compliance.documents.translations.previewEmpty")}
            className="min-h-[20rem]"
          />
        </div>
      </div>

      {error && (
        <p data-testid="compliance-translation-error" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {canMutate && (
        <div className="flex items-center gap-3">
          <Button
            data-testid="compliance-translation-save"
            onClick={handleSave}
            disabled={!isDirty || !isValid || update.isPending}
          >
            {update.isPending ? t("compliance.actions.saving") : t("compliance.actions.save")}
          </Button>
          {isDirty && (
            <p className="text-xs text-muted-foreground">
              {t("compliance.documents.translations.dirtyHint")}
            </p>
          )}
        </div>
      )}
    </CompliancePageShell>
  );
}
